/**
 * Low-level WebSocket client for the LangGraph conversational agent.
 *
 * Mirrors backend/langgraph_agents/protocol.py exactly. Envelope (both
 * directions): { type, request_id, data }. `request_id` is generated here
 * per outgoing message and echoed back by the server on every resulting
 * event, so callers can correlate progress/final events with the request
 * that triggered them.
 */

export const AGENT_MESSAGE_TYPES = {
  // client -> server
  USER_MESSAGE: 'user_message',
  HITL_DECISION: 'hitl_decision',
  PING: 'ping',
  // server -> client
  SESSION_READY: 'session_ready',
  ACK: 'ack',
  THINKING_STEP: 'thinking_step',
  HITL_REQUIRED: 'hitl_required',
  FINAL_ANSWER: 'final_answer',
  ERROR: 'error',
  PONG: 'pong'
};

const DEFAULT_WS_PATH = '/ws/agent/';
const HEARTBEAT_INTERVAL_MS = 25000;
const MAX_RECONNECT_DELAY_MS = 30000;

function resolveWsBaseUrl() {
  const configured = process.env.REACT_APP_WS_BASE_URL;
  if (configured) return configured.replace(/\/$/, '');

  const apiBase = process.env.REACT_APP_API_BASE_URL;
  if (apiBase) {
    try {
      const url = new URL(apiBase);
      const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${wsProtocol}//${url.host}`;
    } catch (_err) {
      // Ignore invalid API base and fallback to window host.
    }
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}`;
}

let requestCounter = 0;
function nextRequestId() {
  requestCounter += 1;
  return `req_${Date.now()}_${requestCounter}`;
}

/**
 * Thin wrapper around the native WebSocket: auth handshake (DRF token as a
 * query param, since browsers can't set custom headers on the WS upgrade),
 * JSON envelope framing, heartbeat, and exponential-backoff reconnection.
 */
export class AgentSocket {
  constructor({ threadId = null, onEvent, onOpen, onClose } = {}) {
    this.threadId = threadId;
    this.onEvent = onEvent || (() => {});
    this.onOpen = onOpen || (() => {});
    this.onClose = onClose || (() => {});
    this.ws = null;
    this.reconnectAttempts = 0;
    this.shouldReconnect = true;
    this.heartbeatTimer = null;
    this.reconnectTimer = null;
    this.connectionTimeout = null;
    
    // Log connection details for debugging
    const token = localStorage.getItem('token');
    const wsBase = resolveWsBaseUrl();
    const path = this.threadId ? `${DEFAULT_WS_PATH}${this.threadId}/` : DEFAULT_WS_PATH;
    const fullUrl = `${wsBase}${path}?token=${token ? '***' : 'MISSING'}`;
  }

  connect() {
    const token = localStorage.getItem('token');
    
    // If no token, wait and retry (user might still be logging in or token might be cleared)
    if (!token) {
      console.warn('[AgentSocket] No token found in localStorage. Retrying in 2s...');
      setTimeout(() => {
        if (this.shouldReconnect) {
          this.connect();
        }
      }, 2000);
      return;
    }
    
    const path = this.threadId ? `${DEFAULT_WS_PATH}${this.threadId}/` : DEFAULT_WS_PATH;
    const wsBase = resolveWsBaseUrl();
    const url = `${wsBase}${path}?token=${encodeURIComponent(token)}`;

    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      console.error('[AgentSocket] Failed to create WebSocket:', err.message);
      console.error('[AgentSocket] This may indicate a browser/network issue. Check Network tab in DevTools.');
      this._scheduleReconnect();
      return;
    }

    // Set a timeout to detect if connection takes too long (15s is reasonable for slow servers)
    this.connectionTimeout = setTimeout(() => {
      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
    }, 15000);

    this.ws.onopen = () => {
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      this.reconnectAttempts = 0;
      this._startHeartbeat();
      this.onOpen();
    };

    this.ws.onmessage = (event) => this._handleMessage(event);

    this.ws.onclose = (event) => {
      if (this.connectionTimeout) {
        clearTimeout(this.connectionTimeout);
        this.connectionTimeout = null;
      }
      
      const statusText = {
        1000: 'Normal Closure',
        1001: 'Going Away',
        1002: 'Protocol Error',
        1003: 'Unsupported Data',
        1006: 'Connection Closed Abnormally',
        1008: 'Policy Violation',
        1009: 'Message Too Big',
        1010: 'Missing Extension',
        1011: 'Internal Error',
        4000: 'Custom Error',
        4401: 'Unauthorized (Auth Failed)',
      }[event.code] || `Unknown Code ${event.code}`;
      
      console.warn(`[AgentSocket] ❌ Connection closed: ${statusText} (Code: ${event.code})`);
      if (event.reason) {
        console.warn(`[AgentSocket]    Reason: ${event.reason}`);
      }
      
      // Diagnostic info
      if (event.code === 1006) {
        console.error('[AgentSocket] 🔧 Diagnostic: Code 1006 typically means:');
        console.error('[AgentSocket]    - Server rejected the connection (check auth token)');
        console.error('[AgentSocket]    - Network connectivity issue');
        console.error('[AgentSocket]    - Daphne/server not running on port 8000');
        console.error('[AgentSocket]    - Nginx not forwarding WebSocket correctly');
      } else if (event.code === 4401) {
        console.error('[AgentSocket] 🔑 Diagnostic: Auth failed');
        console.error('[AgentSocket]    - Token may be invalid or expired');
        console.error('[AgentSocket]    - Check localStorage.getItem("token") in console');
      }
      
      this._stopHeartbeat();
      this.onClose();

      // Code 4401 = Unauthorized (likely token issue)
      if (event.code === 4401) {
        console.error('[AgentSocket] ⚠️  Not reconnecting after auth failure.');
        // Don't reconnect immediately on auth failure
        if (this.shouldReconnect && this.reconnectAttempts < 2) {
          this._scheduleReconnect();
        }
      } else if (event.code === 1000) {
        // Normal closure
      } else {
        // Reconnect on other errors
        this._scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // WebSocket error event fired
    };
  }

  disconnect() {
    this.shouldReconnect = false;
    this._stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    // Only close if socket exists and is not already closed/closing
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED && this.ws.readyState !== WebSocket.CLOSING) {
      this.ws.close(1000, 'Client disconnect');
    }
  }

  /** Starts a brand-new turn. Returns the request_id for correlation. */
  sendUserMessage(text) {
    return this._send(AGENT_MESSAGE_TYPES.USER_MESSAGE, { text });
  }

  /** Resolves a pending HITL interrupt. `decision` matches the backend's
   * HitlDecision contract: { action, selected_option_key?, selected_ids?, free_text? } */
  sendHitlDecision(checkpointId, decision) {
    return this._send(AGENT_MESSAGE_TYPES.HITL_DECISION, { checkpoint_id: checkpointId, decision });
  }

  _send(type, data) {
    const requestId = nextRequestId();
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type, request_id: requestId, data }));
    }
    return requestId;
  }

  _handleMessage(event) {
    let parsed;
    try {
      parsed = JSON.parse(event.data);
    } catch (_err) {
      return;
    }
    this.onEvent(parsed);
  }

  _startHeartbeat() {
    this._stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      this._send(AGENT_MESSAGE_TYPES.PING, {});
    }, HEARTBEAT_INTERVAL_MS);
  }

  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  _scheduleReconnect() {
    if (!this.shouldReconnect) return;
    const delay = Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** this.reconnectAttempts);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect();
      }
    }, delay);
  }
}

export default AgentSocket;
